import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineRounded";
import RefreshIcon from "@mui/icons-material/RefreshRounded";
import { Box, Button, Typography } from "@mui/material";
import type { ErrorComponentProps } from "@tanstack/react-router";

/**
 * Route-level error fallback for TanStack Router's `errorComponent`
 * slot. Catches loader / `beforeLoad` failures and render-time errors
 * inside the route subtree — the difference from `FeatureErrorBoundary`
 * (the React class boundary used to wrap rendered pages) is that the
 * router invokes this *before* the page mounts, so a thrown loader
 * doesn't crash the whole app shell.
 */
export function RouteErrorComponent({ error, reset }: ErrorComponentProps) {
  const message =
    error instanceof Error
      ? error.message
      : "An unexpected error occurred. The rest of the app is still usable.";
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        p: 4,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 2,
          p: 3,
          maxWidth: 480,
          bgcolor: "surface.raised",
          border: "1px solid",
          borderColor: "border.default",
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <ErrorOutlineIcon sx={{ color: "error.main", fontSize: 24 }} />
          <Typography variant="h4" sx={{ color: "text.primary" }}>
            This page failed to load
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", wordBreak: "break-word" }}
        >
          {message}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon fontSize="small" />}
          onClick={reset}
        >
          Try again
        </Button>
      </Box>
    </Box>
  );
}
